import React, { useState } from "react";
import { MdEdit } from "react-icons/md";
import clsx from "clsx";

import AddFrom from "./AddForm";
import EditForm from "./EditForm";
import NoData from "./NoData";
import Loader from "./Loader";

import { ICONS } from "../constants";

const TableBody = ({
  isLoading,
  rowsToDisplay = [],
  columns = [],
  selectedRows = [],
  rowBeingEdited,
  onSelect,
  onDelete,
  onEdit,
  onEditCancel,
  onEditComplete,
  onSaveComplete,
  onSaveCancel,
  noAction,
  Action,
  hasActions,
}) => {
  const [loadingRows, setLoadingRows] = useState([]);

  const addRowToLoading = (__id) => {
    setLoadingRows((prevState) => [...prevState, __id]);
  };

  const removeRowFromLoading = (__id) => {
    setLoadingRows((prevState) => {
      const updatedIds = prevState.filter((id) => id !== __id);
      return updatedIds;
    });
  };

  const getRow = (row, rowIndex) => {
    const lastColumnIndex = columns.length - 1;

    return columns.map((column, columnIndex) => {
      const isLastCol = columnIndex === lastColumnIndex;
      let columnContent = column.key ? row[column.key] : null;
      let colSpan = 1;

      if (column.render) {
        columnContent = column.render(row, rowIndex);
      }

      if (!_renderAction(row, rowIndex) && hasActions && isLastCol) {
        colSpan = 2;
      }

      return (
        <td className={column.className} key={columnIndex} colSpan={colSpan}>
          {columnContent}
        </td>
      );
    });
  };

  const getNextRow = (currentRowId) => {
    const currentRowIndex = rowsToDisplay.findIndex(({ id }) => id === currentRowId);
    if (currentRowIndex === rowsToDisplay.length - 1) return null;
    return rowsToDisplay[currentRowIndex + 1]
  }

  const _renderSelect = (row) => {
    if (!onSelect) return null;
    return (
      <td className="datatable_select">
        <input
          type="checkbox"
          checked={selectedRows.includes(row.__id)}
          onChange={({ target }) => onSelect(row.__id, target.checked)}
        />
      </td>
    );
  };

  const _renderAction = (row, rowIndex) => {
    if (!onDelete && !onEdit && !Action) return null;

    const actions = [];
    const isLoading = loadingRows.includes(row.__id);
    const actionProps = {
      row,
      rowIndex,
      showLoader: () => addRowToLoading(row.__id),
      hideLoader: () => removeRowFromLoading(row.__id),
      getNextRow: getNextRow
    };

    if (onEdit) {
      actions.push(
        <div className='tooltip'>
          <div className="tooltiptext">Edit</div>
          <span
            className="datatable_actions-edit"
            onClick={() => onEdit(row.__id)}
            children={<MdEdit />}
            key={`edit_${rowIndex}`}
          />
        </div>
      );
    }

    if (onDelete) {
      actions.push(
        <span
          className="datatable_actions-del"
          onClick={() => onDelete(row)}
          children={<ICONS.Delete />}
          key={`delete_${rowIndex}`}
        />
      );
    }

    if (Action) {
      actions.push(<Action {...actionProps} key={`action_${rowIndex}`} />);
    }

    return (
      <td className="datatable_actions">{isLoading ? <Loader /> : actions}</td>
    );
  };

  const _renderAddAction = () => {
    return (
      <AddFrom
        colSpan={!!onSelect}
        columns={columns}
        onSave={onSaveComplete}
        onCancel={onSaveCancel}
        key="add-form"
      />
    );
  };

  const _render = () => {
    if (!rowsToDisplay || !rowsToDisplay.length) {
      if (onSaveComplete) return _renderAddAction();

      return (
        <NoData
          isLoading={isLoading}
          columns={columns}
          hasActionColumn={hasActions}
          hasSelectOption={!!onSelect}
        />
      );
    }

    let tableRows = rowsToDisplay.map((row, i) => {
      if (rowBeingEdited === row.__id) {
        return (
          <EditForm
            colSpan={onSelect ? 1 : 0}
            columns={columns}
            data={row}
            rowIndex={i}
            onCancel={onEditCancel}
            onSave={onEditComplete}
            key={row.__id}
          />
        );
      }

      return (
        <tr key={row.__id} className={clsx({ "no-action": noAction })}>
          {_renderSelect(row, i)}
          {getRow(row, i)}
          {_renderAction(row, i)}
        </tr>
      );
    });

    if (onSaveComplete) {
      tableRows = [_renderAddAction(), ...tableRows];
    }

    return tableRows;
  };

  return (
    <tbody
      className={clsx("datatable_grid-body", {
        "open-add-form": !!onSaveComplete,
      })}
      children={_render()}
    />
  );
};

export default TableBody;